import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Search, ChevronDown, ChevronUp } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import { getEquipmentLabel } from "@/i18n/labels/equipmentLabels";
import { CAR_EQUIPMENT_CATEGORIES } from "@/constants/equipmentCategories";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { nativeNestedSheetDialogProps } from "@/lib/native/nativeNestedSheetProps";
import { cn } from "@/lib/utils";

interface EquipmentSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEquipment: string[];
  onEquipmentChange: (equipment: string[]) => void;
  vehicleType?: "car" | "mc";
}

const MC_EQUIPMENT_CATEGORIES = {
  "🛡️ Bromsar & Säkerhet": ["ABS-bromsar", "ABS", "Dynamic Brake Light"],
  "🎮 Styrning & Kontroll": [
    "Vehicle Hold Control (VHC)",
    "Riding modes",
    "Ducati Traction Control (DTC EVO)",
    "Hill Start Pro",
    "Dynamic ESA",
    "Växelassistent",
  ],
  "💡 Belysning": [
    "LED-strålkastare",
    "LED-blinkers",
    "Varselljus",
    "Daytime Running Lights",
  ],
  "🛋️ Komfort & Utrustning": [
    "Värmehandtag",
    "Handskydd",
    "Väskhållare",
    "Kromade krokar",
    "Centralstöd",
    "Packbox ingår",
    "Plant golv",
  ],
  "🖥️ Instrumentering & Teknik": [
    "TFT-display",
    "Analog instrumentering",
    "Digital instrumentering",
    "GPS-förberedelse",
    "Farthållare",
    "Däcktryckskontroll",
    "Keyless Go",
    "Keyless Start",
  ],
  "📞 Säkerhet & Kommunikation": [
    "IEC (Intelligent Emergency Call)",
    "Larm",
    "Stöldlarm",
  ],
  "⚙️ Motor & Drivlina": [
    "Luftkyld",
    "2-takt",
    "4-takt",
    "Start: Elektrisk",
    "Start: Kick",
    "Start: Elektrisk och kick",
  ],
  "🎨 Styling & Design": [
    "Style Exclusive",
    "Ekerhjul",
    "Sportavgassystem",
    "Kromat avgassystem",
  ],
  "🔋 Övrigt": [
    "HP-batteri",
    "Passagerarkit",
    "Ljudsystem",
    "Elektriskt justerbar vindruta",
    "EURO 4",
  ],
};

export const EquipmentSelectionDialog = ({
  open,
  onOpenChange,
  selectedEquipment,
  onEquipmentChange,
  vehicleType = "car",
}: EquipmentSelectionDialogProps) => {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const vLabels = valuationLabels(language).vehicleInfo;
  const [searchQuery, setSearchQuery] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const nestedSheetProps = nativeNestedSheetDialogProps(open, {
    expandable: true,
    form: true,
  });

  const eqLabel = (key: string) => getEquipmentLabel(key, language);

  const EQUIPMENT_CATEGORIES = useMemo(() => {
    return vehicleType === "mc"
      ? MC_EQUIPMENT_CATEGORIES
      : CAR_EQUIPMENT_CATEGORIES;
  }, [vehicleType]);

  const allPredefinedItems = useMemo(
    () => new Set(Object.values(EQUIPMENT_CATEGORIES).flat()),
    [EQUIPMENT_CATEGORIES],
  );

  const customEquipmentList = useMemo(() => {
    return selectedEquipment.filter((item) => !allPredefinedItems.has(item));
  }, [selectedEquipment, allPredefinedItems]);

  const itemMatchesQuery = (item: string, query: string) =>
    !query ||
    item.toLowerCase().includes(query) ||
    getEquipmentLabel(item, language).toLowerCase().includes(query);

  const filteredEquipment = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered: Partial<typeof EQUIPMENT_CATEGORIES> & {
      "📝 Anpassad utrustning"?: string[];
    } = {};

    const categoryMatchesQuery = (cat: string) =>
      !query ||
      cat.toLowerCase().includes(query) ||
      getEquipmentLabel(cat, language).toLowerCase().includes(query);

    Object.entries(EQUIPMENT_CATEGORIES).forEach(([category, items]) => {
      const categoryMatches = categoryMatchesQuery(category);
      const matchingItems = items.filter((item) =>
        itemMatchesQuery(item, query),
      );

      if (categoryMatches || matchingItems.length > 0) {
        filtered[category] = categoryMatches ? items : matchingItems;
      }
    });

    if (customEquipmentList.length > 0) {
      const matchingCustom = customEquipmentList.filter((item) =>
        itemMatchesQuery(item, query),
      );
      if (matchingCustom.length > 0) {
        filtered["📝 Anpassad utrustning"] = matchingCustom;
      }
    }

    return filtered;
  }, [searchQuery, customEquipmentList, EQUIPMENT_CATEGORIES, language]);

  const flatSearchResults = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return [];
    const items: string[] = [];
    Object.values(filteredEquipment).forEach((list) => {
      list?.forEach((item) => {
        if (!items.includes(item)) items.push(item);
      });
    });
    return items;
  }, [searchQuery, filteredEquipment]);

  const hasSearchQuery = searchQuery.trim().length > 0;

  const toggleEquipment = (item: string) => {
    const newSelection = selectedEquipment.includes(item)
      ? selectedEquipment.filter((e) => e !== item)
      : [...selectedEquipment, item];
    onEquipmentChange(newSelection);
    if (!selectedEquipment.includes(item)) {
      setSearchQuery("");
    }
  };

  const addCustomEquipment = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || selectedEquipment.includes(trimmed)) return;
    onEquipmentChange([...selectedEquipment, trimmed]);
    setSearchQuery("");
  };

  const addBulkEquipment = () => {
    const parts = bulkText
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = [...selectedEquipment];
    for (const part of parts) {
      if (!next.includes(part)) next.push(part);
    }
    onEquipmentChange(next);
    setBulkText("");
    setBulkOpen(false);
  };

  const renderEquipmentItem = (item: string, isCustomCategory = false) => {
    const isSelected = selectedEquipment.includes(item);
    return (
      <button
        key={item}
        type="button"
        onClick={() => toggleEquipment(item)}
        className={cn(
          "w-full text-left p-2 rounded-md border transition-colors flex items-center justify-between",
          isSelected
            ? "border-accent border-2 bg-accent/20"
            : "border-border hover:bg-accent/20",
        )}
      >
        <span className={cn("flex-1", isMobile ? "text-xs" : "text-sm")}>
          {eqLabel(item)}
        </span>
        {isCustomCategory && (
          <span
            role="presentation"
            onClick={(e) => {
              e.stopPropagation();
              toggleEquipment(item);
            }}
            className="ml-2 hover:bg-destructive/20 rounded-full p-0.5 flex-shrink-0"
          >
            <X className="h-3 w-3" />
          </span>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        {...nestedSheetProps}
        className={cn(
          nestedSheetProps.className,
          // Web dialog scrolls on itself; native scrolls via `.native-sheet-body`.
          !isNative && "max-h-[90vh] overflow-y-auto",
          isNative
            ? "w-full max-w-none p-0"
            : isMobile
              ? "p-4 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] mx-auto"
              : "p-6 w-full max-w-[39.2rem] mx-auto",
        )}
      >
        <DialogHeader className={cn(isMobile ? "pt-6 pb-3" : "pt-6 pb-4")}>
          <div className="flex items-center justify-center relative">
            <DialogTitle className="text-center">
              {vLabels.selectEquipment}
            </DialogTitle>
            <Button
              onClick={() => onOpenChange(false)}
              variant="default"
              size="sm"
              className="absolute right-8"
            >
              {vLabels.done}
            </Button>
          </div>
        </DialogHeader>

        <div className={cn(isMobile ? "space-y-3" : "space-y-4")}>
          {selectedEquipment.length > 0 && (
            <div className="pb-2 border-b">
              <p className="text-sm font-medium mb-2">
                {(
                  vLabels.selectedEquipmentCount ?? "Vald utrustning ({count})"
                ).replace("{count}", String(selectedEquipment.length))}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedEquipment.map((item) => (
                  <Badge
                    key={item}
                    variant="outline"
                    className="flex items-center gap-1 pr-1 border-2"
                  >
                    <span className={isMobile ? "text-xs" : ""}>
                      {eqLabel(item)}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleEquipment(item)}
                      className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="sticky top-0 bg-background z-10 py-2 space-y-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={
                  vLabels.searchOrAddEquipmentPlaceholder ??
                  "Sök eller lägg till egen..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  const query = searchQuery.trim();
                  if (!query) return;
                  // Prefer selecting the first search hit; otherwise add as custom
                  if (flatSearchResults.length > 0) {
                    toggleEquipment(flatSearchResults[0]);
                  } else {
                    addCustomEquipment(query);
                  }
                }}
                className={cn(
                  "pl-9",
                  searchQuery ? "pr-9" : "",
                  isMobile ? "text-base min-h-[44px]" : "",
                )}
                style={isMobile ? { fontSize: "16px" } : undefined}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 hover:bg-muted rounded-full p-1"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setBulkOpen((v) => !v)}
              className="text-sm text-blue-600 hover:text-blue-500 font-medium flex items-center gap-1"
            >
              {bulkOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {vLabels.addMultipleEquipment ?? "Lägg till flera samtidigt"}
            </button>

            {bulkOpen && (
              <div className="space-y-2 rounded-lg border p-3 bg-muted/30">
                <p className="text-xs text-muted-foreground">
                  {vLabels.addMultipleEquipmentHint ??
                    "Separera med komma, t.ex. Backkamera, NAV, Panorama tak, AC"}
                </p>
                <Input
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addBulkEquipment();
                    }
                  }}
                  placeholder={
                    vLabels.addMultipleEquipmentPlaceholder ??
                    "Backkamera, NAV, AC..."
                  }
                  className={isMobile ? "text-base min-h-[44px]" : ""}
                  style={isMobile ? { fontSize: "16px" } : undefined}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!bulkText.trim()}
                  onClick={addBulkEquipment}
                  className="bg-blue-600 text-white hover:bg-blue-500"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {vLabels.addButton}
                </Button>
              </div>
            )}
          </div>

          {hasSearchQuery ? (
            <div className="space-y-2">
              {flatSearchResults.length > 0 ? (
                <div
                  className={cn(
                    "grid gap-2",
                    isMobile ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2",
                  )}
                >
                  {flatSearchResults.map((item) => renderEquipmentItem(item))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  {vLabels.noEquipmentSearchResults ??
                    "Inget resultat hittades."}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                className="w-full border-blue-600/40 text-blue-600 hover:bg-blue-600/10"
                disabled={!searchQuery.trim()}
                onClick={() => addCustomEquipment(searchQuery)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {vLabels.addCustomFromSearch ?? "Lägg till egen"}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(filteredEquipment).map(([category, items]) => {
                const isCustomCategory = category === "📝 Anpassad utrustning";
                return (
                  <div key={category} className="border rounded-lg">
                    <div className="w-full flex items-center justify-between p-3 pb-4">
                      <span className="font-medium text-sm">
                        {eqLabel(category)}
                      </span>
                    </div>
                    <div className="p-3 pt-0 space-y-2">
                      <div
                        className={cn(
                          "grid gap-2",
                          isMobile ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2",
                        )}
                      >
                        {items.map((item) =>
                          renderEquipmentItem(item, isCustomCategory),
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {vLabels.close}
            </Button>
            <Button onClick={() => onOpenChange(false)}>{vLabels.done}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
